import React, { useState } from 'react';
import { XMarkIcon, DocumentTextIcon, TrophyIcon } from '@heroicons/react/24/outline';

interface Certificate {
  id: string;
  title: string;
  description: string;
  image: string;
  fullImage: string;
  category: 'water' | 'gas' | 'industrial' | 'management';
}

const certificates: Certificate[] = [
  {
    id: 'cert-water-kz',
    title: 'Сертификат на трубы для водоснабжения (КАЗ)',
    description: 'Сертификат соответствия на полиэтиленовые трубы для систем водоснабжения',
    image: '/images/certificates/С-т водпр-д Каз.pdf',
    fullImage: '/images/certificates/С-т водпр-д Каз.pdf',
    category: 'water'
  },
  {
    id: 'cert-gas-kz',
    title: 'Сертификат на трубы для газоснабжения (КАЗ)',
    description: 'Сертификат соответствия на полиэтиленовые трубы для систем газоснабжения',
    image: '/images/certificates/С-т газпр-д Каз.pdf',
    fullImage: '/images/certificates/С-т газпр-д Каз.pdf',
    category: 'gas'
  },
  {
    id: 'cert-quality',
    title: 'Система менеджмента качества',
    description: 'Сертификат соответствия ISO 9001:2015 для производства полиэтиленовых труб',
    image: '/images/certificates/certificate-2.jpg.pdf',
    fullImage: '/images/certificates/certificate-2.jpg.pdf',
    category: 'management'
  }
];

const CertificatesSection = () => {
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);

  const openModal = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
  };

  const closeModal = () => {
    setSelectedCertificate(null);
  };

  return (
    <section className="py-16 bg-gray-50 dark:bg-secondary-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
            <TrophyIcon className="h-5 w-5 mr-1" /> Сертификаты
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Наши сертификаты и лицензии
          </h2>
          <p className="text-xl text-gray-600 dark:text-secondary-300 max-w-3xl mx-auto">
            Подтверждение соответствия международным и национальным стандартам качества для производства полиэтиленовых труб
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {certificates.map((certificate) => (
            <div
              key={certificate.id}
              className="bg-white dark:bg-secondary-800 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => openModal(certificate)}
            >
              <div className="aspect-w-4 aspect-h-5 bg-gray-100 dark:bg-secondary-700 overflow-hidden">
                <div className="w-full h-64 bg-gray-100 dark:bg-secondary-700 overflow-hidden">
                  <object 
                    data={certificate.image}
                    type="application/pdf"
                    className="w-full h-full"
                    style={{ transform: 'scale(0.6)', transformOrigin: 'top left' }}
                  >
                    <div className="w-full h-full bg-gray-100 dark:bg-secondary-700 flex items-center justify-center">
                      <div className="text-center">
                        <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500 dark:text-gray-400 text-xs">PDF превью</p>
                      </div>
                    </div>
                  </object>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                  {certificate.title}
                </h3>
                <p className="text-gray-600 dark:text-secondary-300 text-sm line-clamp-3">
                  {certificate.description}
                </p>
                <div className="mt-4">
                  <span className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium">
                    Посмотреть сертификат
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">4</div>
              <div className="text-sm text-gray-600 dark:text-secondary-300">Водоснабжение</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">4</div>
              <div className="text-sm text-gray-600 dark:text-secondary-300">Газоснабжение</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">3</div>
              <div className="text-sm text-gray-600 dark:text-secondary-300">ISO стандарты</div>
            </div>
          </div>
          <a 
            href="/certificates" 
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Посмотреть все сертификаты
            <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>

      {/* Modal */}
      {selectedCertificate && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={closeModal}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-75" />
            
            <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-secondary-800 shadow-xl rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {selectedCertificate.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 dark:text-secondary-400 hover:text-gray-600 dark:hover:text-secondary-300 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <div className="w-full h-96 bg-gray-50 dark:bg-secondary-700 rounded overflow-hidden">
                  <object 
                    data={selectedCertificate.fullImage}
                    type="application/pdf"
                    className="w-full h-full"
                  >
                    <div className="w-full h-full bg-gray-50 dark:bg-secondary-700 rounded flex items-center justify-center">
                      <div className="text-center">
                        <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 text-sm mb-2">PDF документ</p>
                        <a 
                          href={selectedCertificate.fullImage}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          Открыть в новой вкладке
                          <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </object>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-secondary-300">
                {selectedCertificate.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CertificatesSection;